import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Maximize2, 
  Minimize2, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut, 
  Camera,
  Settings,
  AlertCircle,
  Loader2,
  Move
} from 'lucide-react';

/**
 * Theta360Viewer - Interactive 360° video viewer for RICOH THETA cameras
 * Supports: THETA X, Z1, SC2 via USB UVC mode
 * 
 * Features:
 * - Drag to pan around the 360° view
 * - Scroll/pinch to zoom
 * - Touch support for mobile
 * - Keyboard navigation (arrow keys)
 * - Fullscreen mode
 */
const Theta360Viewer = ({ 
  stream,           // MediaStream from THETA camera
  isActive = true,  // Whether viewer is active
  className = '',
  onError,
  showControls = true
}) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const glRef = useRef(null);
  const programRef = useRef(null);
  const textureRef = useRef(null);
  
  // View state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewState, setViewState] = useState({
    yaw: 0,      // Horizontal rotation (0-360)
    pitch: 0,    // Vertical rotation (-90 to 90)
    fov: 90      // Field of view (30-120)
  });
  
  // Interaction state
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const touchDistance = useRef(0);

  // WebGL shaders for equirectangular projection
  const vertexShaderSource = `
    attribute vec4 aPosition;
    attribute vec2 aTexCoord;
    varying vec2 vTexCoord;
    void main() {
      gl_Position = aPosition;
      vTexCoord = aTexCoord;
    }
  `;

  const fragmentShaderSource = `
    precision mediump float;
    uniform sampler2D uTexture;
    uniform float uYaw;
    uniform float uPitch;
    uniform float uFov;
    varying vec2 vTexCoord;
    
    #define PI 3.14159265359
    
    void main() {
      // Convert screen coordinates to spherical
      float fovRad = uFov * PI / 180.0;
      float aspectRatio = 16.0 / 9.0;
      
      // Screen position to ray direction
      vec2 screenPos = (vTexCoord - 0.5) * 2.0;
      screenPos.x *= aspectRatio;
      
      float x = screenPos.x * tan(fovRad / 2.0);
      float y = screenPos.y * tan(fovRad / 2.0);
      float z = 1.0;
      
      // Normalize
      vec3 ray = normalize(vec3(x, y, z));
      
      // Rotate by pitch (around X axis)
      float pitchRad = uPitch * PI / 180.0;
      float cosP = cos(pitchRad);
      float sinP = sin(pitchRad);
      ray = vec3(ray.x, ray.y * cosP - ray.z * sinP, ray.y * sinP + ray.z * cosP);
      
      // Rotate by yaw (around Y axis)
      float yawRad = uYaw * PI / 180.0;
      float cosY = cos(yawRad);
      float sinY = sin(yawRad);
      ray = vec3(ray.x * cosY + ray.z * sinY, ray.y, -ray.x * sinY + ray.z * cosY);
      
      // Convert to equirectangular UV coordinates
      float theta = atan(ray.x, ray.z);  // -PI to PI
      float phi = asin(clamp(ray.y, -1.0, 1.0));  // -PI/2 to PI/2
      
      vec2 uv;
      uv.x = (theta / PI + 1.0) / 2.0;  // 0 to 1
      uv.y = (phi / (PI / 2.0) + 1.0) / 2.0;  // 0 to 1
      
      gl_FragColor = texture2D(uTexture, uv);
    }
  `;

  // Initialize WebGL
  const initWebGL = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const gl = canvas.getContext('webgl', { 
      preserveDrawingBuffer: true,
      antialias: true 
    });
    
    if (!gl) {
      setError('WebGL non supporté par votre navigateur');
      return false;
    }

    glRef.current = gl;

    // Create shaders
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      setError('Erreur d\'initialisation du shader WebGL');
      return false;
    }

    programRef.current = program;
    gl.useProgram(program);

    // Create vertices for full-screen quad
    const vertices = new Float32Array([
      -1, -1, 0, 1,
       1, -1, 1, 1,
      -1,  1, 0, 0,
       1,  1, 1, 0,
    ]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const aPosition = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 16, 0);

    const aTexCoord = gl.getAttribLocation(program, 'aTexCoord');
    gl.enableVertexAttribArray(aTexCoord);
    gl.vertexAttribPointer(aTexCoord, 2, gl.FLOAT, false, 16, 8);

    // Create texture
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    textureRef.current = texture;

    return true;
  }, []);

  // Render frame
  const render = useCallback(() => {
    const gl = glRef.current;
    const program = programRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!gl || !program || !video || !canvas || video.readyState < 2) {
      animationRef.current = requestAnimationFrame(render);
      return;
    }

    // Update canvas size
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }
    }

    // Update texture with video frame
    gl.bindTexture(gl.TEXTURE_2D, textureRef.current);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

    // Update uniforms
    const uYaw = gl.getUniformLocation(program, 'uYaw');
    const uPitch = gl.getUniformLocation(program, 'uPitch');
    const uFov = gl.getUniformLocation(program, 'uFov');
    
    gl.uniform1f(uYaw, viewState.yaw);
    gl.uniform1f(uPitch, viewState.pitch);
    gl.uniform1f(uFov, viewState.fov);

    // Draw
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    setIsLoading(false);
    animationRef.current = requestAnimationFrame(render);
  }, [viewState]);

  // Initialize video and WebGL when stream changes
  useEffect(() => {
    if (!stream || !isActive) return;

    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    videoRef.current = video;

    video.onloadedmetadata = () => {
      video.play();
      if (initWebGL()) {
        animationRef.current = requestAnimationFrame(render);
      }
    };

    video.onerror = () => {
      setError('Erreur de lecture du flux vidéo');
      onError?.('Video playback error');
    };

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      video.srcObject = null;
    };
  }, [stream, isActive, initWebGL, render, onError]);

  // Mouse/Touch handlers
  const handleMouseDown = (e) => {
    isDragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;

    const deltaX = e.clientX - lastMouse.current.x;
    const deltaY = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };

    setViewState(prev => ({
      ...prev,
      yaw: (prev.yaw - deltaX * 0.3) % 360,
      pitch: Math.max(-85, Math.min(85, prev.pitch + deltaY * 0.3))
    }));
  }, []);

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setViewState(prev => ({
      ...prev,
      fov: Math.max(30, Math.min(120, prev.fov + e.deltaY * 0.05))
    }));
  }, []);

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      isDragging.current = true;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      // Pinch zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchDistance.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isDragging.current) {
      const deltaX = e.touches[0].clientX - lastMouse.current.x;
      const deltaY = e.touches[0].clientY - lastMouse.current.y;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

      setViewState(prev => ({
        ...prev,
        yaw: (prev.yaw - deltaX * 0.3) % 360,
        pitch: Math.max(-85, Math.min(85, prev.pitch + deltaY * 0.3))
      }));
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const delta = touchDistance.current - distance;
      touchDistance.current = distance;

      setViewState(prev => ({
        ...prev,
        fov: Math.max(30, Math.min(120, prev.fov + delta * 0.1))
      }));
    }
    e.preventDefault();
  }, []);

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const step = e.shiftKey ? 10 : 5;
      switch (e.key) {
        case 'ArrowLeft':
          setViewState(prev => ({ ...prev, yaw: (prev.yaw + step) % 360 }));
          break;
        case 'ArrowRight':
          setViewState(prev => ({ ...prev, yaw: (prev.yaw - step) % 360 }));
          break;
        case 'ArrowUp':
          setViewState(prev => ({ ...prev, pitch: Math.min(85, prev.pitch + step) }));
          break;
        case 'ArrowDown':
          setViewState(prev => ({ ...prev, pitch: Math.max(-85, prev.pitch - step) }));
          break;
        case '+':
        case '=':
          setViewState(prev => ({ ...prev, fov: Math.max(30, prev.fov - 5) }));
          break;
        case '-':
          setViewState(prev => ({ ...prev, fov: Math.min(120, prev.fov + 5) }));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Reset view
  const resetView = () => {
    setViewState({ yaw: 0, pitch: 0, fov: 90 });
  };

  // Zoom controls
  const zoomIn = () => {
    setViewState(prev => ({ ...prev, fov: Math.max(30, prev.fov - 10) }));
  };

  const zoomOut = () => {
    setViewState(prev => ({ ...prev, fov: Math.min(120, prev.fov + 10) }));
  };

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 text-white ${className}`}>
        <div className="text-center p-6">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
          <p className="text-lg font-medium">{error}</p>
          <p className="text-sm text-gray-400 mt-2">
            Vérifiez que la caméra RICOH THETA est connectée en mode USB
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative bg-black overflow-hidden ${className}`}
      data-testid="theta-360-viewer"
    >
      {/* Canvas for WebGL rendering */}
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
          <div className="text-center text-white">
            <Loader2 size={48} className="mx-auto mb-4 animate-spin text-[#00D3A8]" />
            <p className="font-medium">Initialisation de la vue 360°...</p>
            <p className="text-sm text-gray-400 mt-1">RICOH THETA</p>
          </div>
        </div>
      )}

      {/* Controls overlay */}
      {showControls && !isLoading && (
        <>
          {/* Top bar - Camera info */}
          <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/60 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Camera size={18} className="text-[#00D3A8]" />
                <span className="text-sm font-medium">Vue 360° - RICOH THETA</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-xs">
                <Move size={14} />
                <span>Glissez pour naviguer</span>
              </div>
            </div>
          </div>

          {/* Bottom controls */}
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={zoomOut}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                title="Zoom arrière"
              >
                <ZoomOut size={20} />
              </button>
              
              <div className="px-3 py-1 bg-white/20 rounded-lg text-white text-sm">
                {Math.round(90 / viewState.fov * 100)}%
              </div>
              
              <button
                onClick={zoomIn}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                title="Zoom avant"
              >
                <ZoomIn size={20} />
              </button>
              
              <div className="w-px h-6 bg-white/30 mx-2" />
              
              <button
                onClick={resetView}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                title="Réinitialiser la vue"
              >
                <RotateCcw size={20} />
              </button>
              
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
              >
                {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            </div>
          </div>

          {/* View position indicator */}
          <div className="absolute top-3 right-3 bg-black/50 rounded-lg px-2 py-1 text-white text-xs">
            {Math.round(viewState.yaw)}° / {Math.round(viewState.pitch)}°
          </div>
        </>
      )}
    </div>
  );
};

export default Theta360Viewer;
